/**
 * OverlayScrollbar — Thunderbird-style auto-hide scrollbar overlay.
 *
 * Wraps a scrollable container and renders a custom thumb on top.
 * Hides the native scrollbar entirely. Works in ALL browsers (Firefox, Chrome, Safari).
 *
 * States:
 *   hidden  → invisible (opacity 0)
 *   thin    → 4px wide translucent gray line (mouse in container or scroll wheel)
 *   thick   → 8px wide, more opaque (mouse directly on scrollbar track)
 *   active  → 8px wide, brand primary color (mouse-down drag)
 *
 * Usage:
 *   <OverlayScrollbar className="flex-1 p-2">
 *     {nav items here}
 *   </OverlayScrollbar>
 *
 * @layer widget
 * @package @plerity/dashboard-shared
 */

import { clsx } from "clsx";
import { type ReactNode, useId } from "react";
import { useOverlayScrollbar } from "../../hooks/useOverlayScrollbar";

export interface OverlayScrollbarProps {
  children: ReactNode;
  className?: string;
  /** data-testid for the wrapper */
  testId?: string;
}

export function OverlayScrollbar({ children, className, testId }: OverlayScrollbarProps) {
  const {
    containerRef,
    thumbTop,
    thumbHeight,
    hasOverflow,
    visibility,
    onThumbMouseDown,
    onTrackClick,
    onTrackEnter,
    onTrackLeave,
  } = useOverlayScrollbar();

  // The role="scrollbar" track's aria-controls MUST resolve to a real element
  // id (axe aria-valid-attr-value, CRITICAL). It previously pointed at
  // `${testId}-content`, an id nothing rendered. useId() is also unique per
  // instance, so two sidebars (desktop + mobile drawer) never collide.
  const contentId = useId();

  // Keyboard equivalent for the role="scrollbar" track below (WAI-ARIA
  // scrollbar pattern requires this widget to be keyboard-operable).
  const onTrackKeyDown = (e: React.KeyboardEvent) => {
    const el = containerRef.current;
    if (!el) return;
    const pageStep = el.clientHeight * 0.9;
    switch (e.key) {
      case "ArrowUp":
        e.preventDefault();
        el.scrollTop -= 40;
        break;
      case "ArrowDown":
        e.preventDefault();
        el.scrollTop += 40;
        break;
      case "PageUp":
        e.preventDefault();
        el.scrollTop -= pageStep;
        break;
      case "PageDown":
        e.preventDefault();
        el.scrollTop += pageStep;
        break;
      case "Home":
        e.preventDefault();
        el.scrollTop = 0;
        break;
      case "End":
        e.preventDefault();
        el.scrollTop = el.scrollHeight;
        break;
    }
  };

  return (
    <div className="relative flex-1 min-h-0" data-testid={testId}>
      {/* Scrollable container — native scrollbar hidden via .hide-native-scrollbar class.
           The class applies scrollbar-width:none (Firefox) + ::-webkit-scrollbar{display:none}
           (Chromium/Safari). Both rules defined in the app's index.css. */}
      <div
        ref={containerRef}
        id={contentId}
        data-testid={testId ? `${testId}-content` : undefined}
        className={clsx("hide-native-scrollbar h-full overflow-y-auto", className)}
      >
        {children}
      </div>

      {/* Custom scrollbar overlay */}
      {hasOverflow && (
        <div
          className={clsx(
            "absolute top-0 right-0 bottom-0 z-10",
            "transition-[width,opacity] duration-300 ease-in-out",
            visibility === "hidden" && "w-1 opacity-0 pointer-events-none",
            visibility === "thin" && "w-1 opacity-100",
            visibility === "thick" && "w-2 opacity-100",
            visibility === "active" && "w-2 opacity-100"
          )}
          onClick={onTrackClick}
          onKeyDown={onTrackKeyDown}
          onMouseEnter={onTrackEnter}
          onMouseLeave={onTrackLeave}
          role="scrollbar"
          tabIndex={0}
          aria-orientation="vertical"
          aria-valuenow={Math.round(thumbTop * 100)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-controls={contentId}
        >
          {/* Track background — subtle on hover */}
          <div
            className={clsx(
              "absolute inset-0 rounded-full transition-colors duration-200",
              visibility === "thick" || visibility === "active"
                ? "bg-gray-400/10 dark:bg-gray-500/10"
                : "bg-transparent"
            )}
          />

          {/* Thumb — mouse-only drag handle; the equivalent keyboard operation
              is provided by the parent role="scrollbar" track's onKeyDown above. */}
          {/* biome-ignore lint/a11y/noStaticElementInteractions: pointer-only drag handle; a keyboard-operable equivalent already exists on the parent track (onTrackKeyDown). */}
          <div
            className={clsx(
              "absolute left-0 right-0 rounded-full transition-colors duration-150",
              "cursor-pointer",
              visibility === "thin" && "bg-gray-400/35 dark:bg-gray-400/25",
              visibility === "thick" && "bg-gray-400/60 dark:bg-gray-400/45",
              visibility === "active" && "bg-brand dark:bg-brand",
              visibility === "hidden" && "bg-gray-400/35 dark:bg-gray-400/25"
            )}
            style={{
              top: `${thumbTop * 100}%`,
              height: `${thumbHeight * 100}%`,
              minHeight: "20px",
            }}
            onMouseDown={onThumbMouseDown}
          />
        </div>
      )}
    </div>
  );
}
